import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2 } from "lucide-react";

const FIELDS = [
  ["name", "Trading name"], ["legal_name", "Legal name (on tax invoice)"], ["trn", "TRN (VAT registration no.)"],
  ["phone", "Phone"], ["email", "Email"], ["website", "Website"],
  ["address", "Address"], ["city", "City / Emirate"], ["country", "Country"],
  ["bank_name", "Bank"], ["bank_account_name", "Account name"], ["iban", "IBAN"], ["swift", "SWIFT / BIC"],
];

export function CompanyProfile() {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/company").then((r) => setC(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/company", c);
      setC(data);
      toast.success("Company profile saved - tax invoices updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!c) return null;
  const ro = user?.role !== "admin";
  return (
    <Card className="p-5 mb-6" data-testid="company-profile-card">
      <div className="flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold">Company profile</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Printed on every tax invoice, credit note and statement. TRN and address are required for a UAE VAT-compliant tax invoice.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${k === "address" ? "sm:col-span-2" : ""}`}>
            <Label className="text-xs">{label}</Label>
            <Input value={c[k] || ""} disabled={ro} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`company-${k}-input`} />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
          <Label className="text-xs">Invoice footer note</Label>
          <Input value={c.invoice_footer || ""} disabled={ro} onChange={(e) => setC({ ...c, invoice_footer: e.target.value })} data-testid="company-invoice_footer-input" />
        </div>
      </div>
      {!ro && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-company-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save company profile"}</Button>
        </div>
      )}
    </Card>
  );
}
